<script lang="ts" setup generic="T">
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import type { IconSize } from '@consumer/logic/icons'

export interface Option<T = string | number | boolean> {
  icon?: string | IconDefinition
  iconSize?: string
  label: string
  subtext?: string
  value: T
  disabled?: boolean
  tooltip?: string
}

withDefaults(defineProps<{
  options: Option<T>[]
  compact?: boolean
  pill?: boolean
  name: string
  disabled?: boolean
  size?: 'small' | 'medium' | 'large'
  containerClass?: string
}>(), {  })

const activeTabId = defineModel<T>()
</script>

<template>
  <div
    class="g-toggle-input"
    :class="[containerClass, { disabled, pill, box: !pill }]"
  >
    <template v-if="pill">
      <GButton
        v-for="option in options"
        :key="String(option.value)"
        v-tooltip="option.tooltip"
        pill
        :label="option.label"
        :icon="option.icon"
        :iconSize="(option.iconSize as IconSize | undefined)"
        :variant="option.value === activeTabId ? 'secondary' : 'transparent'"
        :class="{
          'g-toggle-button': true,
          'active': option.value === activeTabId,
          [`g-toggle-button--${option.label.replace(/\s+/g, '-').toLowerCase()}`]: option.label,
        }"
        :name="name"
        size="medium"
        :disabled="disabled || option.disabled"
        @click="activeTabId = option.value"
      />
    </template>
    <template v-else>
      <GButton
        v-for="option in options"
        :key="String(option.value)"
        v-tooltip="option.tooltip"
        class="g-toggle-button"
        :class="{
          active: option.value === activeTabId,
          compact,
          [`g-toggle-button--${option.label.replace(/\s+/g, '-').toLowerCase()}`]: option.label,
        }"
        :name="name"
        type="button"
        secondary
        size="medium"
        :disabled="disabled || option.disabled"
        @click="activeTabId = option.value"
      >
        <GIcon
          v-if="option.icon"
          :icon="option.icon"
          :size="option.iconSize"
          class="g-toggle-button__icon"
        />
        <span v-if="option.label" class="g-toggle-button__label">
          {{ option.label }}
        </span>
      </GButton>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.g-toggle-input {
  &.disabled {
    opacity: 0.5;
  }
}

// Default (non-pill) styles
.g-toggle-input.box {
  @apply flex font-semibold rounded-v2sm border border-grey-primary bg-grey-primary;

  gap: 1px; // The "border" between elements.

  .g-button.g-toggle-button {
    @apply px-4 py-3 flex gap-2 items-center justify-center whitespace-nowrap;

    --color: theme("colors.v2-secondary");
    --color-focus: theme("colors.navy");

    border: none;
    border-radius: 0;
    flex: 1 0 0;
    font-size: inherit;
    min-width: min-content;
    transition-duration: 150ms;

    &.active {
      --color-focus: theme("colors.primary.base");
      --color: var(--color-focus);
      --bg: var(--bg-focus);

      border: 1px solid var(--color-focus);
      z-index: 1;
    }

    &:first-child {
      @apply rounded-l-v2sm;
    }

    &:last-child {
      @apply rounded-r-v2sm;
    }

    &.compact {
      @apply flex flex-col gap-1 items-center py-2;

      height: auto !important;
    }
  }
}

// Pill styles
.g-toggle-input.pill {
  @apply flex border border-grey-primary gap-1 p-1.5 rounded-full;

  align-items: center;
  height: 100%;
  justify-content: center;
  max-width: fit-content;

  .g-button.g-toggle-button {
    border: 0 !important;
    max-height: fit-content;
    outline: 0 !important;

    &:hover {
      @apply bg-white;
    }

    &.active {
      @apply border-0 shadow-none bg-surface-blue;
    }
  }
}
</style>
